import React from 'react';

import {deprecatedTrackEvent} from 'analytics/Analytics';
import {INSIGHTS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {AnalyticsDashboardActions} from 'insights/analytics-dashboards/AnalyticsDashboardActions';
import * as MakeSpecRequest from 'insights/analytics-dashboards/data/MakeSpecRequest';
import {GetAnalyticsDashboard} from 'insights/analytics-dashboards/details/graphql/__generated/GetAnalyticsDashboard';
import {AnalyticsDashboardSpecResponse} from 'insights/analytics-dashboards/RESTAnalyticsDashboardTypes';
import {initializeSQLTemplateForm} from 'insights/analytics-queries/block-editor/BlockEditorReduxActions';
import {FormType} from 'insights/analytics-queries/block-editor/sql-template-form/SQLTemplateFormTypes';
import createDefaultAnalyticsQuery from 'insights/analytics-queries/data/createDefaultAnalyticsQuery';
import {MAX_DASHBOARD_BLOCKS, MAX_DASHBOARD_BLOCKS_MESSAGE} from 'insights/InsightsConstants';
import {getStore} from 'redux-stores/Store';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonProps, ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';
import SectionHeader from 'ui-library/menus/SectionHeader';
import {withLoadingScreen} from 'util/Promises';

type AnalyticsDashboard = ExtractByTypename<
  GetAnalyticsDashboard['analyticsDashboard'],
  'AnalyticsDashboardNode'
>;

export const ADD_BLOCK_BUTTON_TEXT = 'Add block';
export const SQL_QUERY_OPTION_TEXT = 'SQL query';
const FORM_TYPE_TO_OPTION_TEXT: {[k in FormType]: string} = {
  [FormType.BASIC_BIOENTITY]: 'List of entities',
};

const FORM_TYPE_TO_DATA_TEST_KEY: {[k in FormType]: string} = {
  [FormType.BASIC_BIOENTITY]: 'entityList',
};

type Actions = Pick<AnalyticsDashboardActions, 'updateAnalyticsDashboard'> & {
  openBlockEditor: (analyticsQueryId: string) => void;
};
export interface AddBlockToDashboardButtonProps {
  actions: Actions;
  analyticsDashboard: AnalyticsDashboard;
  buttonProps?: Pick<ButtonProps, 'size'>;
}

function findNewAnalyticsQueryId(
  oldDashboard: AnalyticsDashboard,
  newDashboard: AnalyticsDashboardSpecResponse
): string {
  const oldAnalyticsQueryIds = oldDashboard.analyticsQueries.map((analyticsQuery) => analyticsQuery.id);
  const newAnalyticsQuery = newDashboard.analyticsQueries.find(
    (analyticsQuery) => !oldAnalyticsQueryIds.includes(analyticsQuery.id)
  );
  return newAnalyticsQuery!.id;
}

export default function AddBlockToDashboardButton({
  actions,
  analyticsDashboard,
  buttonProps = {size: ButtonSize.SMALL},
}: AddBlockToDashboardButtonProps): JSX.Element | null {
  async function handleAddBlock(formType: FormType | null): Promise<void> {
    const analyticsQuerySpecRequest = createDefaultAnalyticsQuery(analyticsDashboard.analyticsQueries, {
      returnNoResults: formType != null,
    });
    const newDashboard = await withLoadingScreen(
      actions.updateAnalyticsDashboard(
        analyticsDashboard.id,
        MakeSpecRequest.onAddAnalyticsQuery(analyticsDashboard, analyticsQuerySpecRequest)
      ),
      'Adding block...'
    );
    const newQueryId = findNewAnalyticsQueryId(analyticsDashboard, newDashboard);

    // Initialize the sql template form before opening the block editor; otherwise, the block editor will
    // first open in sql editor mode then change to sql template mode once this update goes through
    //
    // Both the Redux dispatch and the Apollo state update cause a re-render after being called, and since
    // we're updating two state systems, any batching approach doesn't work (e.g. create a new redux action
    // to reduce 2 parts of the state).
    if (formType) {
      getStore().dispatch(initializeSQLTemplateForm(newQueryId, formType));
    }
    actions.openBlockEditor(newQueryId);
    deprecatedTrackEvent(INSIGHTS_ANALYTICS_ACTIONS.ADDED_BLOCK, {
      num_blocks: newDashboard.analyticsQueries.length,
      sql_template_form_type: formType,
    });
  }

  const addBlockMenu = (
    <Menu dropLeft={true} width={MenuWidth.MEDIUM} height={MenuHeight.MEDIUM}>
      <Section>
        <Option
          icon="note-code"
          onSelect={() => handleAddBlock(null)}
          dataTestAttrs={{component: 'AddBlockToDashboardButton', key: 'sqlQuery'}}
        >
          {SQL_QUERY_OPTION_TEXT}
        </Option>
      </Section>
      <SectionHeader>Block templates</SectionHeader>
      {Object.keys(FormType).map((formType: FormType) => (
        <Option
          icon="warehouse-table"
          key={formType}
          onSelect={() => handleAddBlock(formType)}
          value={formType}
          dataTestAttrs={{component: 'AddBlockToDashboardButton', key: FORM_TYPE_TO_DATA_TEST_KEY[formType]}}
        >
          {FORM_TYPE_TO_OPTION_TEXT[formType]}
        </Option>
      ))}
    </Menu>
  );

  const hasMaxBlocks = analyticsDashboard.analyticsQueries.length >= MAX_DASHBOARD_BLOCKS;

  return (
    <Tooltip disabled={!hasMaxBlocks} title={MAX_DASHBOARD_BLOCKS_MESSAGE}>
      <Dropdown menu={addBlockMenu}>
        <Button
          {...buttonProps}
          buttonStyle={ButtonStyle.SECONDARY}
          dataTestAttrs={{component: 'AddBlockToDashboardButton', element: 'root'}}
          disabled={hasMaxBlocks}
          icon="compose"
          text={ADD_BLOCK_BUTTON_TEXT}
        />
      </Dropdown>
    </Tooltip>
  );
}
